/**
 * multicajaJornadaDemo.data.ts — Demo historial: hasta 10 puntos × N turnos por día.
 *
 * QUÉ HACE: Catálogo multicaja y cierres por punto (mañana/tarde/noche) con 6 ventas
 * por turno cubriendo efectivo, nequi, daviplata y tarjeta. pc1 conserva el stress-test
 * canónico; pc2–pc10 usan snapshots generados con totales coherentes.
 *
 * QUÉ NO DEBE HACER: Importar React ni mutar localStorage (eso lo hace apply).
 */

import { setStorage } from "@/mocks/helpers/storage";
import { CAJA_PUNTOS_KEY } from "@/mocks/helpers/cajaMulticaja";
import type { CierreCaja, MovimientoCaja, Venta } from "../types/caja.types";
import {
  calcBalanceCajaFisicaSesion,
  calcResumenVentasSesion,
} from "../utils/cajaResumenVentasSesion";
import { getDemoProductLineForPunto } from "./demoInventoryProductMap";
import { enrichJornadaDemoVentas } from "./enrichJornadaDemoVentas";
import {
  buildMay12CierreNoche,
  buildMay12CierreTarde,
  DEMO_CIERRE_MADRUGADA_ID,
  DEMO_CIERRE_NOCHE_ID,
  DEMO_CIERRE_TARDE_ID,
  MAY12_SESION_MAIN_ID,
} from "./may12CajaDemoSeed.data";
import {
  buildDemoShowcaseCreditos,
  buildDemoShowcaseMovimientos,
  buildDemoShowcaseVentas,
} from "./demoShowcaseHistorial.data";

/** Catálogo demo: 10 puntos de venta activos. */
export const MULTICAJA_DEMO_PUNTOS = [
  { id: "pc1", nombre: "Caja principal", orden: 0, activa: true },
  { id: "pc2", nombre: "Caja Bar", orden: 1, activa: true },
  { id: "pc3", nombre: "Caja Farmacia", orden: 2, activa: true },
  { id: "pc4", nombre: "Caja Restaurante", orden: 3, activa: true },
  { id: "pc5", nombre: "Caja Boutique", orden: 4, activa: true },
  { id: "pc6", nombre: "Caja Ferretería", orden: 5, activa: true },
  { id: "pc7", nombre: "Caja Panadería", orden: 6, activa: true },
  { id: "pc8", nombre: "Caja Licores", orden: 7, activa: true },
  { id: "pc9", nombre: "Caja Express", orden: 8, activa: true },
  { id: "pc10", nombre: "Caja Terraza", orden: 9, activa: true },
] as const;

const EMPLEADOS = [
  { id: "emp_3", nombre: "Carlos Ruiz" },
  { id: "emp_6", nombre: "Sofía Castro" },
  { id: "emp_10", nombre: "Luz Marina" },
  { id: "emp_4", nombre: "Dylan Torres" },
  { id: "emp_5", nombre: "Esteban Gómez" },
  { id: "emp_7", nombre: "José Delgado" },
  { id: "emp_9", nombre: "Miriam Beltrán" },
  { id: "emp_11", nombre: "Diana Pinzón" },
  { id: "emp_2", nombre: "Yinet Alarcón" },
];

type TurnoKey = "manana" | "tarde" | "noche" | "madrugada";

interface TurnoPlantilla {
  key: TurnoKey;
  label: string;
  open: string;
  close: string;
  /** Multiplicador de escala por punto (pc2 más chico que pc1). */
  scale: number;
}

const TURNOS: TurnoPlantilla[] = [
  {
    key: "manana",
    label: "Turno mañana",
    open: "T07:50:00.000-05:00",
    close: "T12:30:00.000-05:00",
    scale: 1,
  },
  {
    key: "tarde",
    label: "Turno tarde",
    open: "T14:05:00.000-05:00",
    close: "T18:40:00.000-05:00",
    scale: 0.85,
  },
  {
    key: "noche",
    label: "Turno noche",
    open: "T19:10:00.000-05:00",
    close: "T22:45:00.000-05:00",
    scale: 0.7,
  },
  {
    key: "madrugada",
    label: "Turno madrugada",
    open: "T00:10:00.000-05:00",
    close: "T07:00:00.000-05:00",
    scale: 0.2,
  },
];

/** Horas repartidas dentro de cada turno (6 ventas). */
const VENTA_HORAS: Record<TurnoKey, string[]> = {
  manana: [
    "08:02:00.000",
    "09:15:00.000",
    "10:05:00.000",
    "11:20:00.000",
    "11:55:00.000",
    "12:15:00.000",
  ],
  tarde: [
    "14:12:00.000",
    "15:05:00.000",
    "16:20:00.000",
    "17:10:00.000",
    "18:05:00.000",
    "18:25:00.000",
  ],
  noche: [
    "19:25:00.000",
    "20:10:00.000",
    "21:05:00.000",
    "21:50:00.000",
    "22:15:00.000",
    "22:28:00.000",
  ],
  madrugada: [
    "00:20:00.000",
    "01:15:00.000",
    "02:40:00.000",
    "03:55:00.000",
    "05:05:00.000",
    "06:20:00.000",
  ],
};

type VentaMetodoSpec =
  | { kind: "single"; method: string }
  | { kind: "mixto"; methods: [string, string] };

/** 6 ventas: un método dedicado c/u + dos mixtas para cubrir los 4 métodos. */
const VENTA_METODO_SPECS: VentaMetodoSpec[] = [
  { kind: "single", method: "efectivo" },
  { kind: "single", method: "nequi" },
  { kind: "single", method: "daviplata" },
  { kind: "single", method: "tarjeta" },
  { kind: "mixto", methods: ["efectivo", "nequi"] },
  { kind: "mixto", methods: ["daviplata", "tarjeta"] },
];

const VENTA_PESOS = [0.18, 0.14, 0.12, 0.2, 0.18, 0.18];

function demoCierreId(puntoIndex: number, turnoIndex: number): string {
  if (puntoIndex === 0) {
    return [
      "1778595754641",
      DEMO_CIERRE_TARDE_ID,
      DEMO_CIERRE_NOCHE_ID,
      DEMO_CIERRE_MADRUGADA_ID,
    ][turnoIndex];
  }
  const base = 1778595754651;
  return String(base + (puntoIndex - 1) * 10 + turnoIndex);
}

function distribuirMontos(total: number, pesos: number[]): number[] {
  let asignado = 0;
  return pesos.map((peso, i) => {
    if (i === pesos.length - 1) return Math.max(1_000, total - asignado);
    const monto = Math.round(total * peso);
    asignado += monto;
    return Math.max(1_000, monto);
  });
}

function mixtoBreakdown(
  methods: [string, string],
  total: number,
): Venta["detalleMetodoPago"] {
  const primero = Math.round(total * 0.55);
  return {
    breakdown: [
      { method: methods[0], amount: primero },
      { method: methods[1], amount: total - primero },
    ],
  };
}

function buildTurnoVentasDemo(
  punto: (typeof MULTICAJA_DEMO_PUNTOS)[number],
  turno: TurnoPlantilla,
  turnoIndex: number,
  emp: (typeof EMPLEADOS)[number],
  factor: number,
): Venta[] {
  const sesionId = `sesion_demo_${punto.id}_${turno.key}`;
  const totalTurno = Math.round(180_000 * factor + turnoIndex * 25_000);
  const montos = distribuirMontos(totalTurno, VENTA_PESOS);
  const horas = VENTA_HORAS[turno.key];

  const baseVentas = montos.map((total, i) => {
    const spec = VENTA_METODO_SPECS[i];
    const catalogLine = getDemoProductLineForPunto(punto.id, i);
    const base: Venta = {
      id: `v-${punto.id}-${turno.key}-${i + 1}`,
      fecha: `2026-05-12T${horas[i]}-05:00`,
      total,
      metodoPago: spec.kind === "single" ? spec.method : "mixto",
      productos: [
        {
          productoId: catalogLine.productoId,
          nombre: catalogLine.nombre,
          cantidad: 1,
          precioUnitario: total,
        },
      ],
      usuarioId: emp.id,
      userName: emp.nombre,
      sesionId,
      puntoCajaId: punto.id,
      puntoCajaNombre: punto.nombre,
      estado: "activa",
    };
    if (spec.kind === "mixto") {
      base.detalleMetodoPago = mixtoBreakdown(spec.methods, total);
    }
    return base;
  });

  return enrichJornadaDemoVentas(baseVentas);
}

function buildTurnoMovimientosDemo(
  punto: (typeof MULTICAJA_DEMO_PUNTOS)[number],
  turno: TurnoPlantilla,
  turnoIndex: number,
  emp: (typeof EMPLEADOS)[number],
  factor: number,
  sesionId: string,
): MovimientoCaja[] {
  const movs: MovimientoCaja[] = [
    {
      id: `m-gasto-${punto.id}-${turno.key}`,
      tipo: "gasto",
      monto: Math.round(5_000 * factor),
      motivo: `Gasto operativo ${punto.nombre}`,
      fecha: `2026-05-12${turno.open}`,
      usuarioId: emp.id,
      userName: emp.nombre,
      metodoPago: "efectivo",
      sesionId,
    },
  ];

  if (turnoIndex === 0 && punto.id !== "pc2") {
    movs.unshift({
      id: `m-ingreso-${punto.id}-${turno.key}`,
      tipo: "ingreso",
      monto: Math.round(20_000 * factor),
      motivo: `Fondo adicional ${punto.nombre}`,
      fecha: `2026-05-12${turno.open}`,
      usuarioId: emp.id,
      userName: emp.nombre,
      metodoPago: "efectivo",
      sesionId,
    });
  }

  if (turnoIndex === 1) {
    movs.push({
      id: `m-retiro-${punto.id}-${turno.key}`,
      tipo: "retiro",
      monto: Math.round(12_000 * factor),
      motivo: `Retiro parcial ${punto.nombre}`,
      fecha: `2026-05-12${turno.close}`,
      usuarioId: emp.id,
      userName: emp.nombre,
      metodoPago: "efectivo",
      sesionId,
    });
  }

  return movs;
}

function buildCompactCierre(
  puntoIndex: number,
  punto: (typeof MULTICAJA_DEMO_PUNTOS)[number],
  turno: TurnoPlantilla,
  turnoIndex: number,
): CierreCaja {
  const emp = EMPLEADOS[(puntoIndex + turnoIndex) % EMPLEADOS.length];
  const factor = (1 + puntoIndex * 0.12) * turno.scale;
  const montoInicial = Math.round(20_000 + puntoIndex * 5_000);
  const sesionId = `sesion_demo_${punto.id}_${turno.key}`;
  const ventasSnapshot = buildTurnoVentasDemo(
    punto,
    turno,
    turnoIndex,
    emp,
    factor,
  );
  const movimientosSnapshot = buildTurnoMovimientosDemo(
    punto,
    turno,
    turnoIndex,
    emp,
    factor,
    sesionId,
  );

  const resumen = calcResumenVentasSesion(ventasSnapshot, sesionId);
  const balance = calcBalanceCajaFisicaSesion(
    montoInicial,
    ventasSnapshot,
    sesionId,
    movimientosSnapshot,
  );
  const montoEsperado = balance.montoEsperado;

  const diferencia =
    turnoIndex === 0 && puntoIndex % 3 === 0
      ? -Math.round(8_000 * factor)
      : turnoIndex === 1
        ? Math.round(3_500 * factor)
        : 0;
  const montoCierre = montoEsperado + diferencia;

  return {
    id: demoCierreId(puntoIndex, turnoIndex),
    abiertaEn: `2026-05-12${turno.open}`,
    cerradaEn: `2026-05-12${turno.close}`,
    montoInicial,
    montoCierre,
    diferencia,
    userId: emp.id,
    estado: "cerrada",
    openedByName: emp.nombre,
    closedByName: emp.nombre,
    label: turno.label,
    puntoCajaId: punto.id,
    puntoCajaNombre: punto.nombre,
    sesionId,
    ventasCount: ventasSnapshot.length,
    ventasSnapshot,
    movimientosSnapshot,
    totalVentasEfectivo: balance.totalEfectivoVentas,
    totalVentasGeneral: resumen.total,
    montoEsperado,
    totalRetiros: balance.totalRetiros,
    totalIngresos: balance.totalIngresos,
    totalIngresosEfectivo: balance.totalIngresosEfectivo,
    totalIngresosDigital: balance.totalIngresosDigital,
    ...(diferencia !== 0
      ? {
          motivoDescuadre:
            diferencia > 0 ? "sobrante_desconocido" : "faltante_desconocido",
          notaDescuadre:
            diferencia > 0
              ? "Sobrante menor al contar denominaciones."
              : "Faltante menor al contar denominaciones.",
        }
      : {}),
  };
}

/** Caja principal — turno mañana con 6 ventas (jornada demo, mismo id histórico). */
function buildPc1CierreMananaJornada(): CierreCaja {
  const punto = MULTICAJA_DEMO_PUNTOS[0];
  const turno = TURNOS[0];
  const emp = { id: "emp_1", nombre: "Juan Pérez" };
  const factor = 1.35;
  const montoInicial = 30_000;
  const sesionId = MAY12_SESION_MAIN_ID;
  const ventasSnapshot = [
    ...buildDemoShowcaseVentas(),
    ...buildTurnoVentasDemo(punto, turno, 0, emp, factor).map((v) => ({
      ...v,
      sesionId,
      puntoCajaId: punto.id,
      puntoCajaNombre: punto.nombre,
    })),
  ];
  const movimientosSnapshot = [
    ...buildDemoShowcaseMovimientos(),
    ...buildTurnoMovimientosDemo(punto, turno, 0, emp, factor, sesionId),
  ];
  const creditosSnapshot = buildDemoShowcaseCreditos();
  const resumen = calcResumenVentasSesion(ventasSnapshot, sesionId);
  const balance = calcBalanceCajaFisicaSesion(
    montoInicial,
    ventasSnapshot,
    sesionId,
    movimientosSnapshot,
  );
  const montoEsperado = balance.montoEsperado;
  const montoCierre = montoEsperado;

  return {
    id: demoCierreId(0, 0),
    abiertaEn: "2026-05-12T07:55:00.000-05:00",
    cerradaEn: "2026-05-12T12:30:00.000-05:00",
    montoInicial,
    montoCierre,
    diferencia: 0,
    userId: emp.id,
    estado: "cerrada",
    openedByName: emp.nombre,
    closedByName: emp.nombre,
    label: turno.label,
    puntoCajaId: punto.id,
    puntoCajaNombre: punto.nombre,
    sesionId,
    ventasCount: ventasSnapshot.length,
    ventasSnapshot,
    movimientosSnapshot,
    creditosSnapshot,
    totalVentasEfectivo: balance.totalEfectivoVentas,
    totalVentasGeneral: resumen.total,
    montoEsperado,
    totalRetiros: balance.totalRetiros,
    totalIngresos: balance.totalIngresos,
    totalIngresosEfectivo: balance.totalIngresosEfectivo,
    totalIngresosDigital: balance.totalIngresosDigital,
  };
}

/** Caja principal — turno madrugada (00:10–07:00): negocio con ventas 24h. */
function buildPc1CierreMadrugada(): CierreCaja {
  const punto = MULTICAJA_DEMO_PUNTOS[0];
  const turno = TURNOS[3];
  const emp = EMPLEADOS[8];
  const factor = 0.2;
  const montoInicial = 30_000;
  const sesionId = `sesion_demo_${punto.id}_${turno.key}`;
  const ventasSnapshot = buildTurnoVentasDemo(punto, turno, 3, emp, factor);
  const movimientosSnapshot = buildTurnoMovimientosDemo(
    punto,
    turno,
    3,
    emp,
    factor,
    sesionId,
  );
  const resumen = calcResumenVentasSesion(ventasSnapshot, sesionId);
  const balance = calcBalanceCajaFisicaSesion(
    montoInicial,
    ventasSnapshot,
    sesionId,
    movimientosSnapshot,
  );
  const montoEsperado = balance.montoEsperado;

  return {
    id: demoCierreId(0, 3),
    abiertaEn: `2026-05-12${turno.open}`,
    cerradaEn: `2026-05-12${turno.close}`,
    montoInicial,
    montoCierre: montoEsperado,
    diferencia: 0,
    userId: emp.id,
    estado: "cerrada",
    openedByName: emp.nombre,
    closedByName: emp.nombre,
    label: turno.label,
    puntoCajaId: punto.id,
    puntoCajaNombre: punto.nombre,
    sesionId,
    ventasCount: ventasSnapshot.length,
    ventasSnapshot,
    movimientosSnapshot,
    totalVentasEfectivo: balance.totalEfectivoVentas,
    totalVentasGeneral: resumen.total,
    montoEsperado,
    totalRetiros: balance.totalRetiros,
    totalIngresos: balance.totalIngresos,
    totalIngresosEfectivo: balance.totalIngresosEfectivo,
    totalIngresosDigital: balance.totalIngresosDigital,
  };
}

/** Persiste los 10 puntos en el catálogo multicaja (idempotente). */
export function ensureMulticajaDemoPuntos(): void {
  setStorage(CAJA_PUNTOS_KEY, [...MULTICAJA_DEMO_PUNTOS]);
}

/** 40 cierres demo: 10 cajas × 4 turnos (incl. madrugada 24h), 6 ventas por turno. */
export function buildMulticajaJornadaCierres(): CierreCaja[] {
  const out: CierreCaja[] = [
    buildPc1CierreMadrugada(),
    buildPc1CierreMananaJornada(),
    buildMay12CierreTarde(),
    buildMay12CierreNoche(),
  ];

  for (let i = 1; i < MULTICAJA_DEMO_PUNTOS.length; i++) {
    const punto = MULTICAJA_DEMO_PUNTOS[i];
    for (let t = 0; t < TURNOS.length; t++) {
      out.push(buildCompactCierre(i, punto, TURNOS[t], t));
    }
  }

  return out;
}

/** IDs estables para refresh del seed en localStorage. */
export function getMulticajaDemoCierreIds(): string[] {
  const ids: string[] = [];
  for (let p = 0; p < MULTICAJA_DEMO_PUNTOS.length; p++) {
    for (let t = 0; t < TURNOS.length; t++) {
      ids.push(demoCierreId(p, t));
    }
  }
  return ids;
}
